"use client"

import type { ProfitFormData, ProfitMetrics } from "./data"

export type PerformanceSummaryProps = {
   formData: ProfitFormData
   metrics: ProfitMetrics
   onSegmentChange: (value: string) => void
   onReset: () => void
   formatUSD: (val: number) => string
}

type MetricBarProps = {
   title: string
   valueFormatted: string
   valueColorClass: string
   progressValue: number
   barColorClass: string
   subLeftText: string
   statusText: string
   statusStyleClass: string
   showBorder?: boolean
}

function MetricBar({
   title,
   valueFormatted,
   valueColorClass,
   progressValue,
   barColorClass,
   subLeftText,
   statusText,
   statusStyleClass,
   showBorder = true,
}: MetricBarProps) {
   return (
      <div className={`space-y-2 ${showBorder ? "border-t border-slate-800 pt-3" : ""}`}>
         <div className="flex items-baseline justify-between">
            <span className="text-custom-lg-normal">{title}</span>
            <span className={`text-xl font-extrabold ${valueColorClass}`}>{valueFormatted}</span>
         </div>
         <div className="h-2 w-full rounded-full bg-slate-800">
            <div
               className={`h-2 rounded-full ${barColorClass} transition-all duration-300`}
               style={{ width: `${Math.min(Math.max(progressValue, 0), 100)}%` }}
            ></div>
         </div>
         <div className="flex justify-between text-custom-14-medium">
            <span >{subLeftText}</span>
            <span className={statusStyleClass}>{statusText}</span>
         </div>
      </div>
   )
}

export default function PerformanceSummary({
   formData,
   metrics,
   onSegmentChange,
   onReset,
   formatUSD,
}: PerformanceSummaryProps) {
   const RATIO_METRICS = [
      {
         id: "netProfit",
         title: "Net Profit Margin",
         valueFormatted: `${metrics.netProfitMargin.toFixed(1)}%`,
         valueColorClass: "text-[#ff7456]",
         progressValue: metrics.netProfitMargin,
         barColorClass: "bg-[#f15a29]",
         subLeftText: `Net Dollar Profit: ${formatUSD(metrics.netProfitValue)}`,
         statusText: metrics.netStatus.text,
         statusStyleClass: metrics.netStatus.style,
      },
      {
         id: "grossProfit",
         title: "Gross Profit Margin",
         valueFormatted: `${metrics.grossProfitMargin.toFixed(1)}%`,
         valueColorClass: "text-emerald-400",
         progressValue: metrics.grossProfitMargin,
         barColorClass: "bg-emerald-400",
         subLeftText: `COGS Spent: ${formatUSD(metrics.totalCOGS)}`,
         statusText: metrics.grossStatus.text,
         statusStyleClass: metrics.grossStatus.style,
      },
      {
         id: "primeCost",
         title: "Prime Cost %",
         valueFormatted: `${metrics.primeCostRatio.toFixed(1)}%`,
         valueColorClass: "text-indigo-400",
         progressValue: metrics.primeCostRatio,
         barColorClass: "bg-indigo-400",
         subLeftText: `COGS + Labor: ${formatUSD(metrics.totalCOGS + formData.laborCost)}`,
         statusText: metrics.primeStatus.text,
         statusStyleClass: metrics.primeStatus.style,
      },
   ]
   return (
      <aside className="space-y-6 lg:sticky  lg:col-span-5">
         <div className="space-y-6 rounded-3xl border border-slate-800 bg-slate-900 p-6 text-white shadow-xl sm:p-8">
            <div className="flex flex-col gap-1 space-y-2 border-b border-slate-800 pb-4">
               <h3 className="text-custom-2xl-bold">Step 2: Real-Time Performance</h3>
               <p className="text-custom-sm-normal">How your restaurant ranks</p>
               <button
                  type="button"
                  onClick={onReset}
                  className="rounded-full border border-[#f15a29]/20 bg-[#f15a29]/10 px-3 py-1 text-custom-sm-normal text-[#ff7456] transition-colors hover:bg-[#f15a29]/20"
               >
                  Reset Defaults
               </button>
            </div>
            {/* SELECT RESTAURANT TYPE */}
            <div>
               <label htmlFor="restaurantTypeSelect" className="mb-2 block text-custom-sm-normal ">
                  Compare Against Segment
               </label>
               <select
                  id="restaurantTypeSelect"
                  value={formData.segment}
                  onChange={(e) => onSegmentChange(e.target.value)}
                  className="block w-full rounded-xl border border-slate-700 bg-slate-800 px-4 py-3 text-custom-sm-normal text-white focus:border-[#f15a29] focus:ring-4 focus:ring-[#f15a29]/20"
               >
                  <option value="Casual Dining">Casual Dining (Benchmark Baseline)</option>
                  <option value="Fast Food / QSR">Fast Food / QSR</option>
                  <option value="Fast Casual">Fast Casual</option>
                  <option value="Fine Dining">Fine Dining</option>
                  <option value="Bar / Pub">Bar / Pub</option>
                  <option value="Café / Bakery">Café / Bakery</option>
               </select>
            </div>

            {/* CASH FLOW HIGHLIGHTS */}
            <div className="grid grid-cols-1 gap-4 text-center">
               <div className="rounded-2xl border border-slate-800 bg-slate-800/50 p-4">
                  <span className="text-custom-xs-bold mb-1 block uppercase tracking-widest text-white">
                     Total Monthly Sales
                  </span>
                  <var className="text-xl font-black not-italic text-emerald-400 sm:text-2xl">
                     {formatUSD(metrics.totalRevenue)}
                  </var>
               </div>
               <div className="rounded-2xl border border-slate-800 bg-slate-800/50 p-4">
                  <span className="text-custom-xs-bold mb-1 block uppercase tracking-widest text-white">
                     Total Expenses
                  </span>
                  <var className="text-xl font-black not-italic text-rose-400 sm:text-2xl">
                     {formatUSD(metrics.totalExpenses)}
                  </var>
               </div>
            </div>

            {/* RATIO LIST WITH METRIC PROGRESS BARS */}
            <div className="space-y-5">
               {/* RATIO METRICS LOOP */}
               {RATIO_METRICS.map((metric, index) => (
                  <MetricBar key={metric.id} {...metric} showBorder={index !== 0} />
               ))}

               {/* BREAK EVEN POINT */}
               <div className="space-y-2 border-t border-slate-800 pt-3">
                  <div className="flex items-baseline justify-between">
                     <span className="text-custom-sm-medium">Break-Even Sales Point</span>
                     <span className="text-lg font-extrabold text-amber-400">
                        {formatUSD(metrics.breakEvenAmount)} / mo
                     </span>
                  </div>
                  <p className="text-custom-sm-medium text-slate-400">
                     Target sales required to pay monthly fixed overhead ({formatUSD(metrics.fixedExpenses)}).
                  </p>
               </div>
            </div>
         </div>
      </aside>
   )
}
